// SolutionFlow — the site's solution-configuration timeline verbs as ONE flow
// (the sibling of `useDesignationsFlow`): the change form (a new period), the
// correction form (an existing period), the start-date fix and the period
// delete, with their toasts. ONE overlay state shared by every door that opens
// them — the site registry's Update config act (the Solution card's link and the
// peek's Edit menu) and the history's row menus — so the page and the peek open
// the same form (ADR-036 §7: one registry, doors). The forms' saves are AWAITED:
// a refusal stays on the drawer's line, the drawer closes on success. A
// correction rides the site's row stamp (ADR-084 §4 — a period is a write
// beneath the site), its stale line loading the latest site (a corrected period
// gone meanwhile is the form's row gone); a change is a new period — no stamp.

import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { SiteDetail } from '@ems-app/schemas/sites/responses';
import { StartDateDialog, deleteConsequence } from '../../components/patterns/ReconcilePeekBody.js';
import { readLatest } from '../../components/patterns/forms/readLatest.js';
import { SolutionConfigForm } from '../../components/patterns/SolutionConfigForm.js';
import { toConfigValues } from '../../components/patterns/solutionConfig.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { useToast } from '../../components/ui/Toast.js';
import { fetchSiteDetail, sitesKeys, useDeleteSolutionPeriod, useSolutionChange, useSolutionCorrection, useStartDateCorrection } from '../../hooks/useSites.js';
import { useWeekStart } from '../../hooks/useWeekStart.js';
import { formatDate } from '../../lib/format.js';

type FocusField = Parameters<typeof SolutionConfigForm>[0]['focusField'];

export type SolutionOverlay =
  | { kind: 'form'; mode: 'changed' | 'correct'; periodId: number | null; focusField?: FocusField }
  | { kind: 'startDate'; periodId: number; stamp?: number }
  | { kind: 'delete'; periodId: number }
  | null;

/** The site the timeline hangs on — its stamp rides a correction (ADR-084 §4). */
export type SolutionSite = Pick<SiteDetail, 'id' | 'row_version' | 'solution_history'>;

export interface SolutionFlowInput {
  projectId: string;
  /** The site — its solution timeline (newest first, the detail payload) and the stamp a correction carries. */
  site: SolutionSite;
  /** The site's lifecycle status — an active site keeps a config (the only-period delete guard). */
  status: string;
  suggestions: SiteDetail['suggestions'];
  /** The site's zone — the start-date dialog's "today" is the site's day. */
  timeZone?: string;
}

export function useSolutionFlow({ projectId, site, status, suggestions, timeZone }: SolutionFlowInput): {
  openOverlay: (overlay: SolutionOverlay) => void;
  overlays: ReactNode;
  current: SiteDetail['solution_history'][number] | null;
  deleteBlocked: boolean;
} {
  const { t } = useTranslation('sites');
  const toast = useToast();
  const qc = useQueryClient();
  const siteId = site.id;
  const history = site.solution_history;
  const [overlay, setOverlay] = useState<SolutionOverlay>(null);
  // The start-date dialog takes the site's stamp as it opens — a refetch while it is open never moves it.
  const openOverlay = (next: SolutionOverlay) => setOverlay(next?.kind === 'startDate' ? { ...next, stamp: site.row_version } : next);
  const weekStart = useWeekStart(projectId);
  const change = useSolutionChange(projectId);
  const correct = useSolutionCorrection(projectId);
  const startDate = useStartDateCorrection(projectId);
  const deletePeriod = useDeleteSolutionPeriod(projectId);

  const current = history.find((p) => p.is_current) ?? null;
  const openSuggestions = suggestions.filter((x) => x.status === 'open');
  // An active site must keep a config — deleting its only period is blocked.
  const deleteBlocked = status !== 'draft' && history.length <= 1;

  // The form's seed from the site as it is at the open: the corrected period's values (else the current's), or the
  // current config a change starts from (blanks for none).
  const initialOf = (mode: 'changed' | 'correct', periodId: number | null) => (s: SolutionSite | null) => {
    const h = s?.solution_history ?? [];
    const cur = h.find((p) => p.is_current);
    return toConfigValues((mode === 'correct' ? h.find((p) => p.id === periodId) : undefined) ?? cur ?? {});
  };
  // Load the latest: the site's detail re-read quietly, into the page's entry once it answers; a corrected period
  // gone meanwhile is the form's row gone.
  const reload = (mode: 'changed' | 'correct', periodId: number | null) => async (): Promise<SolutionSite | null> => {
    const fresh = await readLatest(qc, sitesKeys.detail(projectId, siteId), () => fetchSiteDetail(projectId, siteId, true));
    return mode === 'correct' && !fresh.solution_history.some((p) => p.id === periodId) ? null : fresh;
  };

  const overlays = (
    <>
      {overlay?.kind === 'form' && (
        <SolutionConfigForm
          mode={overlay.mode}
          row={site}
          initial={initialOf(overlay.mode, overlay.periodId)}
          reload={reload(overlay.mode, overlay.periodId)}
          targetStart={overlay.mode === 'correct' ? (history.find((p) => p.id === overlay.periodId)?.effective_from ?? null) : null}
          earliestStart={history.length ? history[history.length - 1]!.effective_from : null}
          boundaries={history.map((p) => p.effective_from)}
          suggestions={openSuggestions}
          focusField={overlay.focusField}
          timeZone={timeZone}
          weekStart={weekStart}
          onClose={() => setOverlay(null)}
          onSubmit={async (values, meta) => {
            // Awaited — a refusal throws to the form's line; the form closes the drawer on success.
            if (overlay.mode === 'changed') {
              // A new period beneath the site — an add, no stamp on the wire.
              await change.mutateAsync({ siteId, body: { ...values, effective_from: meta.effective_from!, change_reason: meta.change_reason ?? null } });
              toast.success(t('reconcile.toast.changed', { date: formatDate(meta.effective_from!, 'date') ?? meta.effective_from! }));
            } else {
              await correct.mutateAsync({ siteId, periodId: overlay.periodId!, body: { ...values, row_version: meta.stamp } });
              toast.success(t('reconcile.toast.corrected'));
            }
          }}
          t={t}
        />
      )}

      {overlay?.kind === 'startDate' && history.length > 0 && (
        <StartDateDialog
          history={history}
          periodId={overlay.periodId}
          timeZone={timeZone}
          weekStart={weekStart}
          onClose={() => setOverlay(null)}
          // One value corrected on what the dialog shows — it carries the site's stamp as the dialog OPENED; a
          // refusal stays in the dialog, said by the confirm (a stale one: close, the page has re-read, set it again).
          onSubmit={(effectiveFrom) => startDate.mutateAsync({ siteId, periodId: overlay.periodId, effectiveFrom, rowVersion: overlay.stamp }).then(() => void toast.success(t('reconcile.toast.dateFixed')))}
          t={t}
        />
      )}

      {overlay?.kind === 'delete' && (
        <ConfirmDialog
          open
          onClose={() => setOverlay(null)}
          title={t('reconcile.delete.title')}
          description={deleteConsequence(history, overlay.periodId, t)}
          confirmLabel={t('reconcile.delete.confirm')}
          tone="danger"
          onConfirm={() => deletePeriod.mutateAsync({ siteId, periodId: overlay.periodId }).then(() => void toast.success(t('reconcile.toast.deleted')))}
        />
      )}
    </>
  );

  return { openOverlay, overlays, current, deleteBlocked };
}
